import {orderBy, sortBy, uniqBy} from 'lodash-es';
import {useCallback, useEffect, useState} from 'react';

import {useCountryList} from '@/context/CountryListContext';
import type {CurrencyFilters} from '@/services/currencyService';
import {currencyService} from '@/services/currencyService';
import type {
  ResolverCapabilityMatrix,
  ResolverProviderMap,
} from '@/services/processorService';
import {processorService} from '@/services/processorService';
import type {AfriexCountryCode, AfriexCurrency} from '@/types';
import {CountryStatus} from '@/types';
import {getErrorMessage} from '@/utils/errorMessage';
import {logger} from '@/utils/logger';
import {showErrorMessage} from '@/utils/showErrorMessage';
import {showSuccessMessage} from '@/utils/showSuccessMessage';

import type {UpdateResolversRequest} from './types';

const EMPTY_PROVIDERS: ResolverProviderMap = {
  bank: [],
  mobile: [],
  ach: [],
  card: [],
};

const EMPTY_MATRIX: ResolverCapabilityMatrix = {
  bank: {},
  mobile: {},
  ach: {},
  card: {},
};

const useResolverList = () => {
  const [providers, setProviders] =
    useState<ResolverProviderMap>(EMPTY_PROVIDERS);
  const [matrix, setMatrix] = useState<ResolverCapabilityMatrix>(EMPTY_MATRIX);
  const [currencyList, setCurrencyList] = useState<AfriexCurrency[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [selectedCurrency, setSelectedCurrency] = useState<AfriexCurrency>();
  const [shouldShowUpdateForm, setShouldShowUpdateForm] =
    useState<boolean>(false);
  const {state: countryListState} = useCountryList();
  const {countryList} = countryListState;

  const activeCountries =
    countryList?.filter((c) => c.status === CountryStatus.ACTIVE) || [];

  const filteredCountryList = uniqBy(
    sortBy(activeCountries, 'currency'),
    'currency',
  );

  /**
   * One representative active country per currency, for the flag only. Same
   * reasoning as the Corridors screen: rows come from the currency list, so a
   * currency whose countries are all inactive costs a flag rather than the
   * whole row.
   */
  const countryCodeByCurrency = filteredCountryList.reduce<
    Record<string, AfriexCountryCode>
  >((acc, country) => {
    if (country?.currency) {
      acc[country.currency] = country.iso2;
    }
    return acc;
  }, {});

  /**
   * `params` is requested whole, not `params.resolvers`. The `columns` query
   * parameter becomes a Mongo projection and matches top-level names only; the
   * response whitelist then narrows the subtree to `params.resolvers`.
   */
  const fetchCurrencyList = useCallback(async () => {
    try {
      const limit = 100;
      // Downloading the whole collection is deliberate here: there is one row
      // per currency (`code` is limited to AfriexCurrencyCode plus the crypto
      // symbols, well under 150), so this loop runs once or twice.
      //
      // Do not copy this loop for a collection that grows (transactions, fees,
      // users, audit events). Every page re-runs the server's count and a
      // longer skip, so each page costs more than the last, and the browser
      // ends up holding every row. For those, page on the server and render
      // one page at a time, stream the full range from one cursor (the
      // `?action=DOWNLOAD` exports built on streamCursorToConsumer), or, in a
      // server job, read in `_id` batches the way BatchTransactionProcessor
      // does.
      const collected: AfriexCurrency[] = [];
      let page = 0;
      for (;;) {
        const {data, total} = await currencyService.getList({
          page,
          limit,
          columns: ['name', 'code', 'symbol', 'isActive', 'id', 'params'],
        } as CurrencyFilters & {columns: string[]});
        collected.push(...(data ?? []));
        const isLastPage =
          !data?.length ||
          data.length < limit ||
          collected.length >= (total ?? collected.length);
        // Backstop: a wrong `total` should not spin forever.
        if (isLastPage || page >= 20) {
          break;
        }
        page += 1;
      }
      setCurrencyList(collected);
    } catch (e) {
      showErrorMessage(
        `Fetching currency list failed:${getErrorMessage(e, String(e))}`,
      );
    }
  }, []);

  const fetchProviders = useCallback(async () => {
    try {
      setProviders(await processorService.getResolverProviders());
    } catch (e) {
      // Left empty rather than falling back to the full processor enum: a
      // dropdown that offers a processor with no adapter lets an operator save
      // a value that fails every lookup at request time.
      showErrorMessage(
        `Fetching resolver providers failed:${getErrorMessage(e, String(e))}`,
      );
    }
  }, []);

  const fetchMatrix = useCallback(async () => {
    try {
      setMatrix(await processorService.getResolverMatrix());
    } catch (e) {
      // Left empty for the same reason as the provider list above, and the
      // failure is safe in the same direction: an empty matrix disables every
      // field rather than opening them all up.
      showErrorMessage(
        `Fetching resolver capabilities failed:${getErrorMessage(e, String(e))}`,
      );
    }
  }, []);

  const loadAll = useCallback(async () => {
    try {
      await Promise.all([fetchCurrencyList(), fetchProviders(), fetchMatrix()]);
    } finally {
      setIsLoading(false);
    }
  }, [fetchCurrencyList, fetchProviders, fetchMatrix]);

  useEffect(() => {
    // Both fetchers try/catch into showErrorMessage, so this only fires if that
    // reporting itself threw; dev logger rather than a second toast.
    loadAll().catch(logger.error);
  }, [loadAll]);

  const handleUpdateResolvers = useCallback(
    async ({currency, resolvers}: UpdateResolversRequest) => {
      try {
        if (!currency?.id) {
          showErrorMessage('Updating resolvers failed: currency has no id');
          return;
        }

        // Merged onto the params the document already carries so the fee and
        // referral fields the Settings screen owns survive. Empty selections
        // are dropped rather than stored as '', which is what the server reads
        // as "not configured".
        const nextResolvers = Object.fromEntries(
          Object.entries(resolvers).filter(([, processor]) =>
            Boolean(processor),
          ),
        );

        const saved = await currencyService.saveOne(currency.id, {
          params: {...currency.params, resolvers: nextResolvers},
        });
        if (saved) {
          showSuccessMessage('Resolvers updated successfully');
          setShouldShowUpdateForm(false);
          await fetchCurrencyList();
        }
      } catch (e) {
        showErrorMessage(
          `Updating resolvers failed:${getErrorMessage(e, String(e))}`,
        );
      }
    },
    [fetchCurrencyList],
  );

  const showResolverUpdateForm = useCallback((currency: AfriexCurrency) => {
    setSelectedCurrency(currency);
    setShouldShowUpdateForm(true);
  }, []);

  return {
    isLoading,
    providers,
    matrix,
    countryList: activeCountries,
    countryCodeByCurrency,
    currencyList: orderBy(currencyList, 'code'),
    selectedCurrency,
    shouldShowUpdateForm,
    showResolverUpdateForm,
    closeResolverUpdateForm: () => setShouldShowUpdateForm(false),
    handleUpdateResolvers,
  };
};

export default useResolverList;
